// The gateway's public price list (GET /pricing): Latlens products priced as
// their Mapbox equivalents, converted to naira. One credit is ₦0.10.
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';

export interface PriceBand {
  from: number;
  to: number | null;
  usd_per_1000: number;
  ngn_per_1000: number;
  credits_per_unit: number;
  /** True when an admin set the naira price instead of converting from US$. */
  custom?: boolean;
}

export interface PricedProduct {
  id: string;
  mapbox: string;
  unit: string;
  free_per_month: number;
  bands: PriceBand[];
}

export interface PriceList {
  as_of: string;
  usd_ngn: number;
  credit_ngn: number;
  credits_per_naira: number;
  products: PricedProduct[];
}

/** Credits ₦1 buys; matches the gateway's KOBO_PER_CREDIT of 10. */
export const CREDITS_PER_NAIRA = 10;

export const pricingApi = {
  get: () => apiClient.get<PriceList>('/pricing'),
};

/** Admin price settings (GET/PUT/DELETE /admin/pricing). */
export interface BandSetting { upTo: number | null; usdPer1000: number; ngnPer1000: number | null }
export interface PricingSettings {
  usd_ngn: number;
  as_of: string;
  products: Record<string, { freePerMonth: number; bands: BandSetting[] }>;
  updated_by: string | null;
  updated_at: number | null;
}
export interface AdminPricing {
  settings: PricingSettings;
  is_default: boolean;
  defaults: PricingSettings;
  preview: PriceList;
  services: Record<string, string[]>;
  can_edit: boolean;
}

export const adminPricingApi = {
  get: (): Promise<AdminPricing> => apiClient.get('/admin/pricing').then(r => r.data),
  save: (s: Pick<PricingSettings, 'usd_ngn' | 'products'>): Promise<Pick<AdminPricing, 'settings' | 'is_default' | 'preview'>> =>
    apiClient.put('/admin/pricing', s).then(r => r.data),
  reset: (): Promise<Pick<AdminPricing, 'settings' | 'is_default' | 'preview'>> => apiClient.delete('/admin/pricing').then(r => r.data),
};

/** US$ per 1,000 at a rate, rounded down to the nearest ₦100, as the gateway does. */
export const toNaira = (usdPer1000: number, rate: number) => Math.floor((usdPer1000 * rate) / 100) * 100;

export function usePriceList() {
  return useQuery({
    queryKey: ['pricing'],
    queryFn: async () => (await pricingApi.get()).data,
    staleTime: 60 * 60 * 1000,
  });
}

/** What Latlens calls each priced product. */
export const PRODUCT_LABELS: Record<string, string> = {
  directions: 'Routing and transit trips',
  matrix: 'Route matrix',
  isochrone: 'Isochrones',
  map_matching: 'Map matching',
  optimization: 'Route optimisation',
  vector_tiles: 'Vector map tiles',
  raster_tiles: 'Raster map tiles',
  geocoding: 'Search, reverse search, address codes, transit stops',
  tilequery: 'Nearby features (Overpass), locate, elevation',
};
